/**
 * English elapsed-time prompts and chrome (ADR 0051 part 3). Unchanged from
 * the original inline text in TimeGenerator.ts and Problems-UI/Time.tsx.
 */
export const timeSentences = {
    duration: (name: string, activity: string, start: string, end: string) =>
        `${name}'s ${activity} started at ${start} and ended at ${end}. How long did it last?`,
    end: (name: string, activity: string, start: string, durationText: string) =>
        `${name}'s ${activity} started at ${start} and lasted ${durationText}. What time did it end?`,
    start: (name: string, activity: string, end: string, durationText: string) =>
        `${name}'s ${activity} ended at ${end} after ${durationText}. What time did it start?`,
};

/** name/activity pairs a sentence-mode problem draws from. */
export const sentenceSubjects: [string, string][] = [
    ['Maya', 'reading time'],
    ['Leo', 'soccer practice'],
    ['Ava', 'piano lesson'],
    ['Noah', 'art class'],
    ['Ivy', 'bike ride'],
    ['Sam', 'swimming lesson'],
    ['Ruby', 'walk'],
    ['Max', 'science project'],
];

/** What the worksheet asks, for the number-line display's prompt line. */
export const numberLinePrompts: Record<'duration' | 'end' | 'start', string> = {
    duration: 'How much time passed?',
    end: 'What time did it end?',
    start: 'What time did it start?',
};

export const timeChrome = {
    digitalPrompt: 'How much time passed? ____________',
    afterWord: 'after',
    answerLabel: 'Answer:',
};

/** "2 hours", "1 hour", "45 minutes", "1 hour 35 minutes". No plural on minutes - matches the shipped behaviour. */
export function durationInWords(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hourPart = hours > 0 ? `${hours} hour${hours === 1 ? '' : 's'}` : '';
    const minutePart = minutes > 0 ? `${minutes} minutes` : '';
    if (hourPart && minutePart) {
        return `${hourPart} ${minutePart}`;
    }
    return hourPart || minutePart || '0 minutes';
}

/** What each half of the day prints as, and the number line's 12:00 label (ADR 0090 §4, §5). */
export const dayHalves = { am: 'a.m.', pm: 'p.m.', noon: 'noon' };

/**
 * The A.M.-or-P.M. event bank (ADR 0090 §2). Each event happens on one side
 * of noon in ordinary life, and `from`/`to` (minutes past midnight, both
 * inclusive) is when: always inside one half, never in the 11 or 12 o'clock
 * hours, and always holding a whole hour so an hour sheet can use it. The
 * judgement that an event belongs to one half is made here, where a
 * reviewer can read it next to the window; the test can only hold the
 * windows to those rules. Recess, naps, lunch and sleeping were left out as
 * ambiguous.
 */
export const amPmEvents: { text: string; half: 'am' | 'pm'; from: number; to: number }[] = [
    { text: 'Ava eats breakfast', half: 'am', from: 6 * 60, to: 8 * 60 + 30 },
    { text: 'Leo wakes up for school', half: 'am', from: 6 * 60, to: 7 * 60 + 30 },
    { text: 'The school bus picks up Maya', half: 'am', from: 7 * 60, to: 8 * 60 + 30 },
    { text: 'School starts for Noah', half: 'am', from: 8 * 60, to: 9 * 60 },
    { text: 'The sun comes up and the rooster crows', half: 'am', from: 5 * 60, to: 7 * 60 },
    { text: 'Ivy walks the dog before school', half: 'am', from: 6 * 60 + 30, to: 8 * 60 },
    { text: 'Sam has a math lesson before lunch', half: 'am', from: 9 * 60, to: 10 * 60 + 30 },
    { text: 'Ruby eats dinner', half: 'pm', from: 17 * 60, to: 19 * 60 },
    { text: 'Max goes to bed', half: 'pm', from: 19 * 60, to: 21 * 60 },
    { text: "The stars come out over Leo's house", half: 'pm', from: 20 * 60, to: 22 * 60 },
    { text: 'Ava has an after-school snack', half: 'pm', from: 15 * 60, to: 16 * 60 + 30 },
    { text: 'School lets out for Maya', half: 'pm', from: 14 * 60 + 30, to: 15 * 60 + 30 },
    { text: 'Noah reads a bedtime story', half: 'pm', from: 19 * 60, to: 20 * 60 + 30 },
    { text: 'Ivy has soccer practice after school', half: 'pm', from: 15 * 60 + 30, to: 17 * 60 + 30 },
];

/** "Ava eats breakfast at 7:30." */
export function amPmSentence(event: string, time: string): string {
    return `${event} at ${time}.`;
}
